import React, { useEffect, useState, useRef } from 'react';
import { LoadingOutlined, UserOutlined, BankOutlined, QrcodeOutlined, TeamOutlined, WifiOutlined, PauseCircleOutlined, PlayCircleOutlined, SecurityScanOutlined , ArrowUpOutlined, CheckCircleTwoTone, ApiOutlined, ClockCircleOutlined, BarChartOutlined } from '@ant-design/icons';
import { 
  DatePicker, Spin, Space, Typography, 
  Table, Tag , Statistic, Card, Row, Col, 
  Badge , Button, Modal, Image, message,
  Switch, Divider,
} from 'antd';

import HeroProfile from '../customer/HeroProfile';
import { isTestModeOn } from '../../utils/testMode';
import MoneyTitle from '../customer/MoneyTitle';
import timeSince from '../../utils/TimeSince';
import tag_a_tag_image from '../../images/Strike_Symbol_RGB_White_Magenta.jpg';
import ProductSwitcher from '../../utils/ProductSwitcher';
import {ccyIsoCodeToSymbolMap} from '../../utils/CurrencyUtils';
import StrikeTapsModel from '../../models/StrikeTapsModel';
import ClientGenericTransactionsModel from '../../models/ClientAvailableTransactionsModel';
import { useWithdrawalDetails } from '../../models/useWithdrawalDetails';

import LaunchQRPageButton from './LaunchQRPageButton';
import { ShowCashOutDialog } from './ShowCashOutDialog';
import { CashOutToTeamDialog } from './CashOutToTeamDialog';
import { BalancesList, useBalancesByCurrency, canShowPayoutsButton } from './BankAccountComponents';
import { useClientTeams } from './useClientTeams';
import { DownloadDataReport } from './DownloadDataReport';


import '../../App.scss';
import 'antd/dist/antd.css';

const { Paragraph, Text } = Typography;
const { RangePicker } = DatePicker;
const SpinnerIcon = <LoadingOutlined style={{ fontSize: 16 }} spin />;

export const isLocalHost = window.location.hostname === 'localhost';

export const ccy = ccyIsoCodeToSymbolMap;


// TESTING: Test data to validate datasources
const datasourceForTesting = [
  {
    // REQUIRED fields:
    currency: 'EUR',
    is_test: false,
    product: 'tipping',
    balance: 300, decimalBalance: 3,
    // OPTIONAL fields:
    pending: 100, pendingDecimal: 1,
    available: 200, pendingAvailable: 2,
    withdrawn: 300, withdrawnDecimal: 3,
  },
  {
    currency: 'GBP',
    is_test: false,
    product: 'tipping',
    balance: 400, decimalBalance: 4,
    pending: 200, pendingDecimal: 2,
    available: 0, pendingAvailable: 0,
    withdrawn: 500, withdrawnDecimal: 5,
  },
  {
    currency: 'USD',
    is_test: true,
    product: 'tipping',
    balance: 1400, decimalBalance: 14,
    pending: 200, pendingDecimal: 2,
    available: 200, pendingAvailable: 2,
    withdrawn: 500, withdrawnDecimal: 5,
  }
];

export const PauseButton = ({ isLoading, isRestricted, pauseHandler }) => {
  if (isLoading) {
    return (
      <Button key="1" loading type="secondary">Loading...</Button>
    )
  }
  const buttonText = isRestricted ? 'Enable' : 'Pause';
  const icon = isRestricted ? <PlayCircleOutlined /> : <PauseCircleOutlined />;

  return (
    <Button key="1" icon={icon} onClick={pauseHandler} type="secondary" danger={isRestricted}>
      {buttonText}
    </Button>
  );
}

export const ShowScanTagDialog = ({ visible, handleOk, handleCancel, locStrings, ...rest }) => {
  return (
    <Modal title="Claim Your Page" visible={visible} onOk={handleOk} onCancel={handleCancel}>
      <div style={{ textAlign: 'center' }}>
        <Card >
          <p>Tap a Tag</p>
          <Image src={tag_a_tag_image} width={80} style={{ borderRadius: '50%' }} />
        </Card>
        <p>  </p>
        <p style={{ fontSize: '30px' }} >OR</p>
        <Card>
          <p>Scan a QR</p>
          <QrcodeOutlined style={{ fontSize: '80px' }} />
        </Card>
      </div>
    </Modal>);
};

export const setDefaultPayout = (payouts, clientCurrencyStr) => {
  for (const [i, payout] of payouts.entries()) {
    if (payout && payout.currency === clientCurrencyStr) {
      return i;
    }
  }

  return 0;
}

export const ActionsButtonsCard = ({ visible, handler, locStrings, client, isAdmin, clientId, ...rest }) => {
  const isDeveloper = client && client.isDeveloper ? true : false;
  const isClientTypeTeam = client && client.type === 'team' ? true : false;
  
  let routes = [
  ];

  const viewTeamsRoute = !isClientTypeTeam && client && (!client.hasOwnProperty('teamsAllowed') || (client.hasOwnProperty('teamsAllowed') && client.teamsAllowed))  ?
      [
        {
          icon: <TeamOutlined />,
          route: 'teams',
          text: 'All Teams'
        }
      ]
      : [];

  const idVerificationButton = client && client.idVerificationAllowed && !isClientTypeTeam ?
      [
        {
          icon: <SecurityScanOutlined />,
          route: 'idverification',
          text: 'Identity Verification'
        }
      ]
      : [];

  const profileButton = (isAdmin) ?
      [
        {
          icon: <UserOutlined />,
          route: 'profile',
          text: 'Edit Profile'
        }
      ]
      : [];

  const accessButton = (isAdmin && !isClientTypeTeam) ?
      [
        {
          icon: <TeamOutlined />,
          route: 'access',
          text: 'Manage Access'
        }
      ]
      : [];

  const payouts = (isAdmin && !isClientTypeTeam) ? 
  [
    {
      icon: <BankOutlined />,
      route: 'bank',
      text: 'Payouts'
    },
  ] : [];

  const developer = (isAdmin || isDeveloper) && !isClientTypeTeam ? 
  [
    {
      icon: <ApiOutlined />,
      route: 'developer',
      text: 'Partner'
    },
  ] : [];

  const myQRs = [
    {
      icon: <WifiOutlined rotate='90' />,
      route: 'tags',
      text: 'Pages (QRs)'
    },
  ];

  routes = [
    ...viewTeamsRoute,
    ...profileButton,
    ...myQRs,
    ...payouts,
    ...accessButton,
    ...idVerificationButton,
    ...developer,
  ];

  return visible && (
    <Card align='center'>
      <Paragraph>Select an Option</Paragraph>
      <Space direction='vertical' width={'100%'}>

        { !isClientTypeTeam &&
          <>
            {/* Switch User removed 2026-08-11: the "Clients I Manage" grid opens
                delegated clients, and the DelegatedViewingBanner (layout-level)
                is the always-visible way back — one mental model, not two. */}
            <LaunchQRPageButton
              clientId={clientId}
              product={'payment'}
              path={'payme'}
              title={'Get Paid'}
            />

            <LaunchQRPageButton
              clientId={clientId}
              product={'tipping'}
              path={'tipme'}
              title={'Get Tips'}
            />
          </>
        }
        {
          routes.map((v, i) => (
            <Button
              key={v.route}
              icon={v.icon}
              onClick={() => (handler ? handler(v) : null)}
              type={(i) ? "secondary" : "primary"}
              style={{ textAlign: 'start', width: `100%` }}
            >{v.text}</Button>
          ))
        }
      </Space>
    </Card>
  );
};

export const DownloadTapsReport = ({ client, clientId, updatedTapsRange, ...rest }) => {
  const clientTapsModel = new StrikeTapsModel({ clientId, isTestMode: isTestModeOn(clientId) });
  
  return (
    <DownloadDataReport 
      client={client}
      clientId={clientId}
      model={clientTapsModel}
      updatedDateRange={updatedTapsRange}
      exportButtonText={'Export CSV'}
      subText={'Export payment transactions only'}
    />
  );
};

export const DownloadTransactionsReport = ({ client, clientId, updatedTapsRange, ...rest }) => {
  const clientTxnsModel = new ClientGenericTransactionsModel({ clientId, includeTest: isTestModeOn(clientId) });

  return (
    <DownloadDataReport 
      client={client}
      clientId={clientId}
      model={clientTxnsModel}
      updatedDateRange={updatedTapsRange}
      exportButtonText={'Export CSV'}
      subText={'Export all transactions'}
    />
  );
};

export const SwitchBigRedCloudExport = ({ clientId, toggleExportMode, showBrcCTA, isAdmin, isChecked, ...rest }) => {
  const isLocalHost = window.location.hostname === 'localhost';
  const isTest = isLocalHost;

  const useBRC = isAdmin || true;

  return useBRC && (
    <Space direction='horizontal'>
      <Switch onChange={toggleExportMode} checked={isChecked} defaultChecked />
      <Text>{showBrcCTA}</Text>
    </Space>
  );
};


// MARK: Keeping Profile Image around for another run through tomorrow
export const ProfileImage = ({ condition, locStrings, client, ...rest }) => {
  return condition() ?
    <HeroProfile {...client} {...rest} object={client} locStrings={locStrings} />
    : null;
};


// TODO: When Screen is WIDER show a Grid instead of the Carousel (use `wide` param)
// accoutns is an array of dictionary with : {
//    currency, balance, decimalBalance, is_test, product // required
//    pending, avaliable, withdrawn
//    pendingDecimal, avaliableDecimal, withdrawnDecimal
// }
export const AccountBalancesView = ({ condition, locStrings, client, clientId, payouts, withdrawals, accounts, wide, noButtons, ...rest }) => {
  const teams = useClientTeams(clientId);
  
  const [isCashOutToTeamModalVisible, setIsCashOutToTeamModalVisible] = useState(false);
  const [isCashOutVisible, setIsCashOutVisible] = useState(false);
  const [selectedAvailableAccountId, setAvailableAccountId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [balanceUpdatePending, setBalanceUpdatePending] = useState(false);
  const [latestWithdrawalId, setLatestWithdrawalId] = useState(null);
  
  // Removed toggle state - now using separate buttons

  const [withdrawal] = useWithdrawalDetails(clientId, latestWithdrawalId);

  // Reset balanceUpdatePending when withdrawal completes
  useEffect(() => {
    if (withdrawal && balanceUpdatePending) {
      const completedStatuses = ['sent', 'api_requested', 'batch_requested', 'waiting', 'completed', 'failed', 'refund_failed', 'incomplete'];
      if (completedStatuses.includes(withdrawal.status)) {
        setBalanceUpdatePending(false);
        setLoading(false);
        // Close the modal if it's still open
        if (isCashOutVisible) {
          setIsCashOutVisible(false);
        }
        if (isCashOutToTeamModalVisible) {
          setIsCashOutToTeamModalVisible(false);
        }
      }
    }
  }, [withdrawal, balanceUpdatePending, isCashOutVisible, isCashOutToTeamModalVisible]);

  const showCashOutModal = (availableAccountId) => {
    setAvailableAccountId(availableAccountId)
    setIsCashOutVisible(true);
  };

  const showCashOutToTeamModal = (availableAccountId) => {
    setAvailableAccountId(availableAccountId)
    setIsCashOutToTeamModalVisible(true);
  };

  const handleCashOutOk = () => {
    // setIsCashOutVisible(false);
    setBalanceUpdatePending(true)
  };

  const handleCashOutCancel = () => {
    setIsCashOutVisible(false);
  };

  const handleCashTeamOutOk = () => {
    setIsCashOutToTeamModalVisible(false);
  };

  const handleCashOutTeamCancel = () => {
    setIsCashOutToTeamModalVisible(false);
  };

  // Removed toggle action function - now using separate buttons

  let element = null;

  const testing = false;
  let datasource = testing ? datasourceForTesting : accounts;

  const productSwitcher = new ProductSwitcher({ locStrings });
  const isTransferToTeamAllowed = (client && client.isTransferToTeamAllowed) ? true : false;

  // Filter out accounts with a balance of 0
  // datasource = datasource.filter(account => !!account.total);

  // Check if user can see Payouts button (also allow if any withdrawn > 0)
  const payoutsCheckBase = canShowPayoutsButton(client);
  const hasWithdrawn = (datasource || []).some(acc => (acc.withdrawn || 0) > 0);
  const payoutsCheck = {
    show: payoutsCheckBase.show || hasWithdrawn,
    reason: payoutsCheckBase.reason,
    message: payoutsCheckBase.message,
  };
  const isAdmin = window.location.pathname.includes('/admin/');

  // Build balances-by-currency map (sum available across products)
  const balancesByCurrency = useBalancesByCurrency(datasource || []);
  const hasAnyAvailable = (datasource || []).some(acc => (acc.available || 0) > 0);

  const goToPayouts = () => {
    const isAdmin = window.location.pathname.includes('/admin/');
    const basePath = isAdmin ? '/admin/clients' : '/client/manage';
    window.location.href = `${basePath}/${clientId}/bank`;
  };

  const rowColAccountElement = (
    <div className="accounts-balances-card">
      <div className="d-flex justify-content-between align-items-center mb-4 pt-4">
        <h4 className="mb-0" style={{ fontWeight: 'bold' }}>My funds</h4>
        {(payoutsCheck.show || isAdmin) && (
          <button 
            type="button" 
            className="btn btn-outline-dark btn-sm"
            style={{ marginLeft: '10px', fontSize: '12px', padding: '4px 8px' }}
            onClick={goToPayouts}
            title={!payoutsCheck.show && !isAdmin ? payoutsCheck.message : ''}
          >
            Payouts
          </button>
        )}
      </div>

      {/* List view of balances with default bank accounts */}
      <BalancesList
        accounts={datasource || []}
        bankAccounts={payouts || []}
        client={client}
        loading={loading}
        withdrawals={withdrawals || []}
        clientId={clientId}
        isAdmin={isAdmin}
        isTransferToTeamAllowed={isTransferToTeamAllowed}
        onAddBankAccount={() => goToPayouts()}
        onCashOut={(currency, defaultBank, availableAccountId, type) => {
          if (type === 'team') {
            setAvailableAccountId(availableAccountId);
            setIsCashOutToTeamModalVisible(true);
          } else {
            showCashOutModal(availableAccountId);
          }
        }}
      />

      {/* Modals - placed after list for proper wiring */}
      <ShowCashOutDialog 
        visible={isCashOutVisible && (selectedAvailableAccountId !== null)} 
        setLatestWithdrawalId={setLatestWithdrawalId} 
        loading={loading} 
        setLoading={setLoading} 
        handleOk={handleCashOutOk} 
        handleCancel={handleCashOutCancel} 
        accountId={selectedAvailableAccountId} 
        clientId={clientId} 
        currency={selectedAvailableAccountId ? ccy[datasource.find(acc => acc.availableAccountId === selectedAvailableAccountId)?.currency] : 'EUR'} 
        clientCurrencyStr={selectedAvailableAccountId ? datasource.find(acc => acc.availableAccountId === selectedAvailableAccountId)?.currency : 'EUR'} 
        payouts={payouts} 
        withdrawals={withdrawals} 
        balance={selectedAvailableAccountId ? datasource.find(acc => acc.availableAccountId === selectedAvailableAccountId)?.available : 0} 
        isAdmin={window.location.pathname.includes('/admin/')} 
        {...rest} 
      />
      <CashOutToTeamDialog 
        visible={isCashOutToTeamModalVisible && (selectedAvailableAccountId !== null)} 
        handleOk={handleCashTeamOutOk} 
        handleCancel={handleCashOutTeamCancel} 
        accountId={selectedAvailableAccountId} 
        clientId={clientId} 
        currency={selectedAvailableAccountId ? ccy[datasource.find(acc => acc.availableAccountId === selectedAvailableAccountId)?.currency] : 'EUR'} 
        clientCurrencyStr={selectedAvailableAccountId ? datasource.find(acc => acc.availableAccountId === selectedAvailableAccountId)?.currency : 'EUR'} 
        balance={selectedAvailableAccountId ? datasource.find(acc => acc.availableAccountId === selectedAvailableAccountId)?.available : 0} 
        locStrings={locStrings} 
        {...rest} 
      />
    </div>
  );

  if (datasource.length > 0) {
    element = rowColAccountElement;
  } else {
    element = null;
  }


  return condition() ? element : null;

};


export const RecentTransactionsTable = ({ condition, locStrings, client, accounts, transactions, ...rest }) => {

  if (!condition()) return null;



  const dataSource = [
  ];

  const productSwitcher = new ProductSwitcher({ locStrings });

  transactions.map(
    (transaction, i) => {
      const productMap = productSwitcher.getProductMap(transaction.product);
      return dataSource.push(
        {
          key: `tx-${i}`,
          date: timeSince(Date.parse(transaction.timestamp)), // transaction.timestamp, // 1612816116399
          type: {
            typeName: (transaction.credit > 0 ? productMap.credit.name : productMap.debit.name),
            typeColor: (transaction.credit > 0 ? productMap.credit.color : productMap.debit.color),
            typeDescription: transaction.description,
            productColor: productMap.color,
          },
          currency: transaction.currency,
          value: { currency: transaction.currency, amount: transaction.amount },
          balance: 0,
          description: `${transaction.description} `,
        }
      );
    });


  const columns = [
    {
      title: 'When',
      dataIndex: 'date',
      key: 'date',
      render:
        date => (
          <>
            <Tag color='geekblue'>{date}</Tag>
          </>
        )
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      render:
        type => (
          <>
            <Tag color={type.typeColor}>{type.typeName}</Tag>
          </>
        )
    },
    {
      title: 'Amount',
      dataIndex: 'value',
      align: 'right',
      render:
        value => (
          <div >
            { (value.amount !== 0) &&
              (<MoneyTitle amount={value.amount} currency={value.currency} digits={2} />)
            }
            { (value.amount === 0) &&
              (<Tag color={'blue'}>none</Tag>)
            }
          </div>
        )
    },
  ];

  return condition() ?
    <Table
      style={{ width: '100%' }}
      dataSource={dataSource}
      columns={columns}
      pagination={{ pageSize: 40 }} scroll={{ y: 300 }}
      expandable={{
        expandedRowRender: record => <p style={{ margin: 0 }}>{record.description}</p>,
        rowExpandable: record => record.name !== 'Not Expandable',
      }}
    />
    : null;

};

export const VerifiedClientTag = ({ client, style }) => {

  if(!client.idVerificationStatus || (client.idVerificationStatus && client.idVerificationStatus !== 'verified')){
    return null;
  }

  return  <div style={{textAlign: 'center', width: '100%'}}>
    <Badge count={<CheckCircleTwoTone
                  style={{ fontSize: "18px", strokeWidth: "30", position: 'relative', left:'-7px', top:'-18px', display: 'block'}}
                  twoToneColor={'#1890ff'}
                  />}
    >
      <Tag color={'green'} style={style}>VERIFIED</Tag>
    </Badge>
  </div>

};


// TooltipIcon component to handle tooltips with user creation date
const TooltipIcon = ({ icon, tooltipText, client }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  
  const formatCreatedDate = (agreedTermsAt) => {
    if (!agreedTermsAt) {
      return 'Unknown date';
    }
    
    try {
      // Convert UTC timestamp to local time
      const date = new Date(agreedTermsAt);
      return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Unknown date';
    }
  };

  const getTooltipContent = () => {
    if (icon === 'clock') {
      return 'Pending amount';
    } else if (icon === 'graph') {
      // Check if client and agreedTermsAt exist
      if (!client) {
        return 'Since Unknown date';
      }
      
      if (!client.agreedTermsAt) {
        return 'Since Unknown date';
      }
      
      return `Since ${formatCreatedDate(client.agreedTermsAt)}`;
    }
    return tooltipText;
  };

  return (
    <div 
      className="position-relative d-inline-block"
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      {icon === 'clock' ? (
        <ClockCircleOutlined className="text-muted" style={{ fontSize: '14px' }} />
      ) : icon === 'graph' ? (
        <BarChartOutlined className="text-muted" style={{ fontSize: '14px' }} />
      ) : null}
      
      {showTooltip && (
        <div 
          className="position-absolute bg-dark text-white p-2 rounded"
          style={{
            bottom: '100%',
            left: '50%',
            transform: 'translateX(-50%)',
            whiteSpace: 'nowrap',
            fontSize: '12px',
            zIndex: 1000,
            marginBottom: '5px'
          }}
        >
          {getTooltipContent()}
          <div 
            className="position-absolute"
            style={{
              top: '100%',
              left: '50%',
              transform: 'translateX(-50%)',
              width: 0,
              height: 0,
              borderLeft: '5px solid transparent',
              borderRight: '5px solid transparent',
              borderTop: '5px solid #343a40'
            }}
          />
        </div>
      )}
    </div>
  );
};




